import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { User } from "src/model/class/general classes/user";
import { InnerComponentDataError } from "src/model/enum/enumInnerComponentDataError";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AuthenticationService } from "src/service/authentication.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { isHydrated } from "src/state/hydrate";

@Component({
    selector: "app-list-users",
    templateUrl: "./list-users.component.html",
    styleUrls: ["./list-users.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListUsersComponent implements OnInit, iTranslation {
  private _logContext: string = "List-users.component";
  private _networkServiceSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;

  private _isForcingReloading: boolean = false;

  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public listUsers: Array<User> = new Array<User>();
  public listActiveUsers: Array<User> = new Array<User>();
  public listSelectedUser: Array<User> = new Array<User>();
  public toolBarColor: string = "primary";
  public lblSearchUser: string = "Cerca l'utente...";
  public searchTextUser: string = "";
  public isLoading: boolean = false;
  public showErrorContent: boolean = false;
  public isOnline: boolean = false;

  @Input() allowMultipleSelection: boolean;

  @Output() onListUserSelectedUpdate: EventEmitter<Array<User>> =
    new EventEmitter<Array<User>>();

  /**
* If component is being displaying again after a previous initilisation resulted in a data-fetching error, this flag is TRUE;
* this will result in triggering of the associated service "forceReload" method. Otherwise, it is set to FALSE.
*/
  @Input("isForcingReloading") isForcingReloading: boolean;

  /**
* This emttter is used to signal the "father" component/page that a general error has been launched inside the list
* (USUALLY, this means an error occurred in fetching the displayed data, whhich means an error occurred in the APIs calls
* used to populate the store).
* NOTE: This solution is NECESSARY because the "Error-content.component" can NOT be used directly inside this component, 
* due to circular dependency.
*/
  @Output() errorInUsersList = new EventEmitter<InnerComponentDataError>();

  constructor(
    private _networkService: NetworkService,
    private _translateService: TranslateService,
    private _authService: AuthenticationService,
    private _loggerService: LoggerService,
    private _store: Store
  ) { }

  async ngOnInit() {
    this._isForcingReloading = this.isForcingReloading;

    this.handleFallbackInputParameters();
    this.subscribeNetworkServiceData();

    /*NOTE:
    This invocation of "checkIsStoreReady" is NECESSARY, since the method "loadListUser()" is invoked inside the renderization of the page itself.
    This may lead it to be launched when the "environment.httpServerRoot" is NOT YET INITIALISED.
     */
    await this.checkIsStoreReady();

    await this.loadListUser();

    //NOTE:
    //We write this code to start harmonize the structure of "list-users.component" with the pattern of the other 
    //"list-*.component". 
    //However, this section is commedted FOR NOW, since "forceReloadData()" just straight invocke "loadListUser()" again.
    /*
    if (this._isForcingReloading) {
      this.forceReloadData();
    }
    */
  }

  ngOnDestroy() {
    //this.unsubscribeStoreSelect();

    this._networkServiceSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();
  }

  //#region Translation
  /**
   * Load translation from translation service
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.searchUser"])
      .subscribe((translation) => {
        this.lblSearchUser = translation["dictionary.searchUser"];
      });
  }

  /**
   * Subscribe translation to translation event
   */
  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Get color style selectio if current User is in listSelectedUser list
   *
   * @param user User to check
   * @returns
   */
  public getColorByUserSelection(user: User): string {
    const foundUser: User = this.listSelectedUser.find(
      (us: User) => us.id === user.id
    );

    return foundUser ? this.toolBarColor : "";
  }

  /**
   * Handle click user selection/unselection
   *
   * @param user Environment to check
   */
  public onClickSelectUnselectUser(user: User): void {
    const indexUser: number = this.listSelectedUser.findIndex(
      (us: User) => us.id === user.id
    );

    //De-selection
    if (indexUser >= 0) {
      this.listSelectedUser.splice(indexUser, 1);

      this.onListUserSelectedUpdate.emit(this.listSelectedUser);
    } else {
      //Selection
      if (!this.allowMultipleSelection) {
        this.listSelectedUser = [user];

        this.onListUserSelectedUpdate.emit(this.listSelectedUser);
      } else {
        this.listSelectedUser = [...this.listSelectedUser, user];

        this.onListUserSelectedUpdate.emit(this.listSelectedUser);
      }
    }
  }

  /**
   * Force reload data after "Something went wrong" button refresh click
   */
  public async forceReloadData(): Promise<void> {
    await this.loadListUser();
    this._isForcingReloading = false;
  }

  private async loadListUser(): Promise<void> {
    try {
      this.isLoading = true;


      this.listUsers = await this._authService.asyncgetActiveUsers();

      this.filterUserList();
      this.showErrorContent = false;
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "loadListUser()",
        "ERROR: " + exception.message
      );

      await this.handleApiResponseError();
    } finally {
      this.isLoading = false;
    }
  }

  private async handleApiResponseError(): Promise<void> {
    this.showErrorContent = true;

    this.errorInUsersList.emit(InnerComponentDataError.LIST_USERS);
  }

  private filterUserList(): void {
    //STUB
    this.listActiveUsers = this.listUsers;
  }

  /**
   * Handle fallback input parameter
   */
  private handleFallbackInputParameters(): void {
    this.allowMultipleSelection = this.allowMultipleSelection ??= false;
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.isOnline = !(currentConnectionType === ConnectionType.NONE);

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }

  /**
   * This method check if the NgRx store has already been initialised (IE. hydrated) from the local storage.
   * It does so observing the value of the "hydrated" feature; the method shall not teminate until this feature
   * change its value to TRUE.
   */

  private async checkIsStoreReady(): Promise<boolean> {
    return this._store
      .select(isHydrated)
      .pipe(
        filter((isHydrated: boolean) => isHydrated),
        take(1)
      )
      .toPromise();
  }
}
